import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown } from "lucide-react";

import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { formatUyMinor } from "@/lib/formatUyMinor";
import {
  QUOTE_LINE_ITEM_KINDS,
  QUOTE_LINE_ITEM_KIND_LABEL_KEYS,
  QUOTE_UR_TYPE_LABEL_KEYS,
  quoteDetailRevealsMore,
  type QuoteLineItemView,
} from "@/services/quoteLineItems";

export interface QuoteBreakdownProps {
  lineItems: QuoteLineItemView[] | null;
  currency: string;
  pricesIncludeIva: boolean;
}

/**
 * A quote's itemisation, for whoever is reading it — the consumer comparing
 * bids and the professional reviewing their own (spec rfq-structured-quotes
 * Q3 + Q4). Deliberately one component: the same lines named the same way on
 * both sides is the whole point of storing them.
 *
 * The subtotals are always visible. Three bids side by side are compared on
 * "how much of this is fee and how much is stamps", which the subtotals answer
 * in one glance; the per-line detail matters once a candidate is chosen.
 *
 * THE DETAIL TOGGLE ONLY APPEARS WHEN IT REVEALS SOMETHING. With one line per
 * kind — the ordinary quote — the detail list repeated the subtotals verbatim,
 * so "ver detalle" showed the reader the rows they were already looking at.
 * The professional's own wording is the one thing it did add, so that moves
 * inline under its subtotal and the toggle is kept for the cases where real
 * information is hidden: several lines inside one kind, or a line priced in UR.
 *
 * Renders nothing for a quote submitted before breakdowns existed. Those
 * quotes have no itemisation to show, and an empty "breakdown" section would
 * read as a professional who declined to give one.
 */
export function QuoteBreakdown({
  lineItems,
  currency,
  pricesIncludeIva,
}: QuoteBreakdownProps): JSX.Element | null {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  // Kind order is the constant's order, not the order the professional
  // happened to type: fee first, then statutory, then third-party — so the
  // same two bids always line up when read down.
  const subtotals = useMemo(() => {
    if (lineItems == null) return [];
    return QUOTE_LINE_ITEM_KINDS.map((kind) => ({
      kind,
      amountMinor: lineItems
        .filter((item) => item.kind === kind)
        .reduce((sum, item) => sum + item.amountMinor, 0),
    })).filter((row) => row.amountMinor > 0);
  }, [lineItems]);

  /**
   * The line behind each subtotal, when there is exactly one. Its description
   * is the professional's own wording — "Escritura de compraventa, incluye
   * lectura" — and that is the only thing the detail list was adding in the
   * common case.
   */
  const soleLineByKind = useMemo(() => {
    const byKind = new Map<string, QuoteLineItemView | null>();
    for (const item of lineItems ?? []) {
      byKind.set(item.kind, byKind.has(item.kind) ? null : item);
    }
    return byKind;
  }, [lineItems]);

  /**
   * IS THERE ANYTHING LEFT TO REVEAL? With one line per kind the detail list
   * repeated the subtotals verbatim — same labels, same amounts — so a toggle
   * promising "ver detalle" delivered the rows the reader was already looking
   * at. It earns its place only when a kind holds SEVERAL lines (the split is
   * genuinely hidden) or a line was priced in UR (the conversion is), and the
   * descriptions move inline when it does not.
   */
  const detailAddsSomething = useMemo(() => quoteDetailRevealsMore(lineItems), [lineItems]);

  if (lineItems == null || lineItems.length === 0) return null;

  return (
    // No top border. A component does not get to decide how it is separated
    // from whatever precedes it: inside the consumer's expanded bid row that
    // rule landed directly under the panel's own border and read as a double
    // line above "Honorarios". The one caller that needs a divider supplies
    // it.
    <div className="space-y-2">
      <dl className="space-y-2">
        {subtotals.map((row) => {
          // Only when the toggle is gone: otherwise the description would
          // appear here AND in the detail list, which is the duplication this
          // is undoing.
          const sole = detailAddsSomething ? null : soleLineByKind.get(row.kind);
          const description = sole?.description?.trim();
          return (
            <div key={row.kind} className="flex items-baseline justify-between gap-4 text-sm">
              <dt className="min-w-0 text-muted-foreground">
                <span className="block">{t(QUOTE_LINE_ITEM_KIND_LABEL_KEYS[row.kind])}</span>
                {description ? (
                  <span className="block truncate text-xs text-muted-foreground/80">
                    {description}
                  </span>
                ) : null}
              </dt>
              <dd className="shrink-0 tabular-nums text-foreground">
                {formatUyMinor(row.amountMinor, currency)}
              </dd>
            </div>
          );
        })}
      </dl>

      <p className="text-xs text-muted-foreground">
        {pricesIncludeIva ? t("quoteBreakdown.ivaIncluded") : t("quoteBreakdown.ivaExcluded")}
      </p>

      {detailAddsSomething ? (
        <Collapsible open={open} onOpenChange={setOpen}>
          <CollapsibleTrigger className="flex items-center gap-2 rounded-md text-xs font-medium text-foreground underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            <ChevronDown
              className={`size-3.5 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
            />
            {open ? t("quoteBreakdown.detailHide") : t("quoteBreakdown.detailShow")}
          </CollapsibleTrigger>
          <CollapsibleContent>
            <ul className="mt-2 space-y-2">
              {lineItems.map((item, index) => (
                <li
                  // The lines have no id of their own — they are a jsonb array on
                  // the quote, ordered as submitted — so the index IS the
                  // identity here. Nothing reorders or filters this list.
                  key={`${index}-${item.description}`}
                  className="flex items-baseline justify-between gap-4 text-sm"
                >
                  <span className="min-w-0">
                    <span className="block truncate text-foreground">{item.description}</span>
                    <span className="block text-xs text-muted-foreground">
                      {t(QUOTE_LINE_ITEM_KIND_LABEL_KEYS[item.kind])}
                      {item.urUnits != null && item.urType != null
                        ? ` · ${t("quoteBreakdown.urUnitsHint", {
                            units: item.urUnits,
                            index: t(QUOTE_UR_TYPE_LABEL_KEYS[item.urType]),
                            value: formatUyMinor(item.urValueMinorAtQuote ?? 0, currency),
                          })}`
                        : ""}
                    </span>
                  </span>
                  <span className="shrink-0 tabular-nums text-foreground">
                    {formatUyMinor(item.amountMinor, currency)}
                  </span>
                </li>
              ))}
            </ul>
          </CollapsibleContent>
        </Collapsible>
      ) : null}
    </div>
  );
}
